/**
 * WebGL2 sorts unified splats on the CPU. A wind-reactive splat object re-copies its work buffer
 * every frame it sways (its clock is a component parameter), and PlayCanvas 2.21 asks for a full
 * CPU sort after every re-copy. At 1.5M splats on an RTX 4080 that was 270 sorts a second and a
 * quarter of the frame rate, and far more on a phone. This drops that one request, and only when
 * it can't change the sort.
 *
 * How PlayCanvas 2.21.2 asks for a CPU sort (GSplatManager.update), and what this touches:
 * - Camera movement: `testCameraMovedForSort`. Untouched.
 * - A new world state, from `GSplatWorld.update` → `_updateWorldState`. Untouched; it sets
 *   `manager.sortNeeded` before the bake runs. A new world state comes from any of:
 *   - a placement's resource changing `centersVersion`, `numSplats`, format or modifier hash
 *     (`GSplatPlacementStateTracker`), e.g. `GSplatContainer.update(n, true)` as the clouds
 *     refresh their CPU centres while they drift;
 *   - placements added or removed;
 *   - LOD and streaming changes;
 *   - `scene.gsplat.dirty`.
 *   The new state also re-feeds the changed centres to the sorter (`_feedCpuSorterCenters`).
 * - A format change: `syncFormat`. Untouched.
 * - Re-copies: `GSplatWorld.bake` → `applyWorkBufferUpdates` returns true when any
 *   `GSplatInfo.update()` does. That happens for a world transform change, a placement
 *   `dirtyVersion` change (setParameter / markDirty), `WORKBUFFER_UPDATE_ALWAYS`, or a format
 *   change. Only this request is ever dropped.
 *
 * Its result is dropped only when every check below passes, so whatever changes the sorter's
 * inputs (CPU centres, transforms, the set of splats) keeps its sort, whichever path asks:
 * - the renderer sorts on the CPU (WebGPU sorts on the GPU every frame and is left alone);
 * - the caller names held placements (the wind's swaying objects, only on frames it pushed
 *   their clock);
 * - the bake is for the same world state as this world's previous bake (version and splat
 *   count), and no splat's resource, `centersVersion`, `numSplats` or format changed since;
 * - no splat's world transform changed and none re-copies on every frame;
 * - every splat the bake re-copies is a held placement. So a drifting cloud layer, an animated
 *   effect or a moved model in the same frame keeps the sort, as without wind.
 *
 * Engine internals read: `renderer.gsplatDirector.camerasMap` → `layersMap` → `gsplatManager` /
 * `gsplatManagerShadow`, `world.bake` and `world.getState`, and each GSplatInfo's `node`,
 * `previousWorldTransform`, `_getDirtySource()`, `_lastDirtyVersion`, `_lastFormatVersion`,
 * `resource`. If anything is missing, the sort is kept.
 */
import type * as pc from 'playcanvas';

/** PlayCanvas WORKBUFFER_UPDATE_ALWAYS (scene/constants.js). */
const WORKBUFFER_UPDATE_ALWAYS = 2;

interface DirtySourceLike {
    dirtyVersion?: number;
    workBufferUpdate?: number;
}
interface SplatInfoLike {
    node?: { getWorldTransform(): pc.Mat4 };
    previousWorldTransform?: pc.Mat4;
    resource?: { centersVersion?: number; numSplats?: number; format?: { extraStreamsVersion?: number } | null } | null;
    _getDirtySource?: () => DirtySourceLike | null | undefined;
    _lastDirtyVersion?: number;
    _lastFormatVersion?: number;
}
interface WorldLike {
    bake?: (version: number, camera: unknown, updateBounds: boolean, result: { sortNeeded?: boolean }) => unknown;
    getState?: (version: number) => { splats?: SplatInfoLike[] } | undefined;
}
interface ManagerLike {
    world?: WorldLike;
    renderer?: { usesGpuSort?: boolean };
}
interface DirectorLike {
    camerasMap?: Map<unknown, { layersMap?: Map<unknown, { gsplatManager?: ManagerLike | null; gsplatManagerShadow?: ManagerLike | null }> }>;
}

/** The sort inputs of one splat as of a bake: its resource and that resource's versions. */
interface SortInputs {
    resource: unknown;
    centersVersion: number;
    numSplats: number;
    formatVersion: number;
}
/** A world's previous bake: the state it baked and every splat's sort inputs. */
interface BakeMemo {
    version: number;
    inputs: SortInputs[];
}

const patched = new WeakSet<object>();

const inputsOf = (splat: SplatInfoLike): SortInputs => ({
    resource: splat.resource ?? null,
    centersVersion: splat.resource?.centersVersion ?? -1,
    numSplats: splat.resource?.numSplats ?? -1,
    formatVersion: splat.resource?.format?.extraStreamsVersion ?? 0,
});
const sameInputs = (a: SortInputs | undefined, b: SortInputs): boolean =>
    !!a && a.resource === b.resource && a.centersVersion === b.centersVersion && a.numSplats === b.numSplats && a.formatVersion === b.formatVersion;

/**
 * True when the bake of `version` can only re-copy held placements and nothing the sorter reads
 * changed (see the file comment). Always records this bake's inputs in `memo`.
 */
export function bakeChangesOnlyHeld(world: WorldLike, version: number, held: ReadonlySet<object>, memo: BakeMemo): boolean {
    const splats = world.getState?.(version)?.splats;
    if (!splats) {
        memo.version = NaN;
        memo.inputs = [];
        return false;
    }
    let ok = memo.version === version && memo.inputs.length === splats.length;
    const inputs: SortInputs[] = [];
    for (let i = 0; i < splats.length; i++) {
        const s = splats[i];
        const now = inputsOf(s);
        inputs.push(now);
        if (!sameInputs(memo.inputs[i], now)) ok = false;
        if (!ok) continue;
        // a moved splat (or one the engine can't tell) changes the sort
        if (!s.node || !s.previousWorldTransform || !s.previousWorldTransform.equals(s.node.getWorldTransform())) ok = false;
        // a format change re-copies (and re-sorts through syncFormat as well)
        else if (s.resource?.format && s._lastFormatVersion !== s.resource.format.extraStreamsVersion) ok = false;
        else {
            const source = s._getDirtySource?.();
            if (!source || typeof source.dirtyVersion !== 'number') ok = false;
            else if (source.workBufferUpdate === WORKBUFFER_UPDATE_ALWAYS) ok = false;
            // re-copied this bake: only the held placements may be
            else if (s._lastDirtyVersion !== source.dirtyVersion && !held.has(source)) ok = false;
        }
    }
    memo.version = version;
    memo.inputs = inputs;
    return ok;
}

/**
 * Wrap every unified splat manager's bake. New managers appear with new cameras or layers, so
 * call this each frame; it patches each world once. `heldPlacements()` returns the placements
 * whose re-copies this frame only moved splats in the work buffer (the wind's swaying objects),
 * or null to hold nothing.
 */
export function holdParameterOnlySorts(app: pc.AppBase, heldPlacements: () => ReadonlySet<object> | null): number {
    const director = (app.renderer as unknown as { gsplatDirector?: DirectorLike | null } | undefined)?.gsplatDirector;
    let count = 0;
    director?.camerasMap?.forEach((cameraData) => cameraData.layersMap?.forEach((layerData) => {
        for (const manager of [layerData.gsplatManager, layerData.gsplatManagerShadow]) {
            const world = manager?.world;
            if (!manager || !world || typeof world.bake !== 'function' || typeof world.getState !== 'function') continue;
            count++;
            if (patched.has(world)) continue;
            patched.add(world);
            const bake = world.bake;
            const memo: BakeMemo = { version: NaN, inputs: [] };
            world.bake = function (this: WorldLike, version, camera, updateBounds, result) {
                const held = manager.renderer?.usesGpuSort ? null : heldPlacements();
                // always compare (and record) the inputs, so a held frame is judged against the previous bake
                const hold = bakeChangesOnlyHeld(this, version, held ?? EMPTY, memo) && !!held && held.size > 0;
                const out = bake.call(this, version, camera, updateBounds, result);
                if (hold && result) result.sortNeeded = false;
                return out;
            };
        }
    }));
    return count;
}

const EMPTY: ReadonlySet<object> = new Set();
